/* Import components */

import React from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import { defaultColumnsOption } from '../config';
import paginationFactory from 'react-bootstrap-table2-paginator';

/**
 * DataTable Component
 * @param {Array} data - The table data
 * @param {Array} columns - Column definitions
 * @param {Function} onTableChange - Callback for table change events (e.g., sorting)
 * @param {Array} defaultSorted - Default sorting configuration
 * @param {String} keyField - Unique key field for rows
 * @param {String} noDataIndication - Message to show when no data is available
 */

export const DataTablePaginated = ({
	data = [],
	columns = [],
	onTableChange,
	onAddTask,
	onRowClick,
	onPrepayment,
	sizePerPage = '100',
	onPageChange,   
	onGenerateInvoice,
	actions,
	defaultSorted,
	keyField = '_id',
	noDataIndication = 'No data found',
	classes,
	rowClasses,
	totalRecords = 0,
	totals = null, // DEPRECATED: Use totalRowData instead
	totalRowData = null, // Pre-built total row data object mapping dataFields to JSX content
}) => {

	/* Define action column formatter */
	
	const columnDataFormatter = (cell, row, rowIndex, formatExtraData) => {
	  return (
		<div className="row align-items-center">
			{onAddTask && <span onClick={() => onAddTask(row)} className="col btn-link-page">Add task</span>}
			{onPrepayment && <span onClick={() => onPrepayment(row)} className="col btn-link-page">Prepayment</span>}
		</div>
	  );
	};
	
	/* Define action column */
	
	const columnFormatter = {
		dataField: '',
		text: 'Actions',
		classes: 'cell-actions',
		headerClasses: 'cell-actions',
		formatter: columnDataFormatter,
	};
	
	/* Define default sort caret */
	
	const defaultSortCaret = (order, column) => (
		<span className="order">
			<span className={`caret caret-up${order === 'asc' ? ' active' : ''}`} />
			<span className={`caret caret-down${order === 'desc' ? ' active' : ''}`} />
		</span>
	);
	
	/* Define table row click events */
	/*
	const tableEvents = {
		events: {
			onClick: (e, column, columnIndex, row, rowIndex) => {
				if (!navigateTo) return;
				if (dataKey === 'documents') {
				window.open(`${navigateTo}/${row._id}/edit`, '_blank');
				} else {
				push(`${navigateTo}/${row._id}`);
				}
			},
		},
	};
	*/
	
	const formatDuration = (minutes) => {
		const m = minutes || 0;
		const hours = Math.floor(m / 60);
		const mins = Math.round(m % 60);
		return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
	};

	const getRowClass = row => {
		// Special styling for total row
		if (row._isTotalRow) {
			return 'table-row total-row-item';
		}
		return `table-row${rowClasses ? ' ' + rowClasses(row) : ''}`;
	};

	const rowEvents = onRowClick
		? {
			onClick: (e, row) => {
				if (!row._isTotalRow && onRowClick) onRowClick(row, e);
			},
		}
		: undefined;

	// Use pre-built totalRowData prop (or fall back to null if not provided)
	const finalTotalRowData = totalRowData || null;

	// Prepend total row to data if totalRowData is provided and there is data
	const tableData = finalTotalRowData && data.length > 0 
		? [{ _isTotalRow: true, _id: '__total__', ...finalTotalRowData }, ...data]
		: data;

	// Enhance columns with total row handling
	const enhancedColumns = columns.map(column => {
		const originalFormatter = column.formatter;
		
		return {
			...column,
			formatter: (cell, row, rowIndex, formatExtraData) => {
				// If this is a total row, return the value for this column from finalTotalRowData
				if (row._isTotalRow && finalTotalRowData) {
					const totalValue = finalTotalRowData[column.dataField];
					// Return the value if it exists (it's already JSX or null)
					return totalValue || null;
				}
				// Otherwise use the original formatter
				if (originalFormatter) {
					return originalFormatter(cell, row, rowIndex, formatExtraData);
				}
				return cell;
			}
		};
	});

	return (
		<BootstrapTable
			hover
			striped
			keyField         = {keyField} // Unique identifier for rows
			noDataIndication = {noDataIndication} // Show when no data is available
			data             = {tableData} // Table data with total row prepended
			// columns       = {columns} // Table columns
			columns          = {enhancedColumns
				.map((column) => ({
					...column,
					sort: typeof column.sort === 'undefined' ? true : column.sort,
					sortCaret: column.sortCaret || defaultSortCaret,
				}))
				//.concat({ ...actions, ...columnFormatter })
			}
			classes          = 'sheet-table' // Custom class for the table
			remote           = {{ sort: true }} // Enable remote sorting only
			pagination       = {false} // Disable internal pagination - use external pagination
			rowClasses       = {getRowClass}
			{...(rowEvents && { rowEvents })}
			defaultSorted    = {defaultSorted} // Set default sorting configuration
			onTableChange    = {onTableChange} // Optional: Callback for sorting, filtering, etc.
		/>
	);
};
